import { ReactElement, useState } from 'react';
import { Component as CartIcon } from 'icons/icon-ui--cart.svg';
import { Component as CartAwarenessIcon } from 'icons/icon-ui--cart-awareness.svg';
import { gtmTrackBeginCheckout, gtmTrackViewCart } from '../../services/gtm/gtm';
import { CartStatus } from '../../models/cart.types';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { CartDrawerLoading } from './CartDrawerLoading';
import { CartDrawer } from './CartDrawer';
import { EmptyCartDrawer } from './EmptyCartDrawer';
import { useNotification } from '../../contexts/NotificationContext';
import { useUser } from '../../contexts/UserContext';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import styles from './CartDrawer.module.scss';
import SignupDrawerContainer from '../signup-drawer/SignupDrawerContainer';
import { SignupStep } from '../../constants/signup';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  setSignInModalVisible?: (val: boolean) => void;
}

function CartDrawerContainer({ setSignInModalVisible = undefined }: Props): ReactElement {
  const {
    cart,
    isCartLoading,
    isCartDrawerOpen,
    setIsCartDrawerOpen,
    currentSignupStep,
    setCurrentSignupStep,
    displayCartUpdateAwareness,
  } = useMenuWithCart();
  const { showNotification } = useNotification();
  const {
    user,
    isActive,
  } = useUser();
  const [cartStatus, setCartStatus] = useState<CartStatus | undefined>(undefined);

  const isSkipped = cart?.isSkipped;
  const totalCartQuantity = isSkipped ? 0 : cart?.cartItems?.reduce(
    (itemCount, cartItem) => itemCount + (cartItem?.quantity || 0), 0,
  );
  const { isLoggedIn } = useAuth();
  const handleOpen = () => {
    const buttonElement = document.activeElement as HTMLElement;
    buttonElement.blur(); // remove focus from the cart button so the drawer can trap focus
    setIsCartDrawerOpen(true);

    if (cart && !isLoggedIn) {
      gtmTrackViewCart(cart);
    }
  };

  const handleClose = (closeBySave = false) => {
    setIsCartDrawerOpen(false);
    if (closeBySave) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_SAVED} />,
        duration: 5000,
      });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`${styles.cart_drawer_button} ${isSkipped || !isActive ? styles.is_skipped : ''}`}
      >
        <CartIcon
          width="23"
          focusable="false"
        />
        <div className={`${styles.cart_total} ${isSkipped || !isActive ? styles.is_skipped : ''}`}>
          {cart?.priceSummary?.total || '$0.00'}
        </div>
        <span
          className={`${styles.cart_item_count} ${isSkipped || !isActive ? styles.is_skipped : ''}`}
        >
          {totalCartQuantity}
        </span>
        {displayCartUpdateAwareness && (
          <CartAwarenessIcon
            focusable="false"
            className={styles.updated_cart}
          />
        )}
      </button>

      {/* render 1 of 3 possible cart drawers depending on cart state */}
      {/* or render the signup drawer if in the signup flow */}
      {isCartLoading && isCartDrawerOpen && !cart?.cartItems?.length && (
        <CartDrawerLoading
          isDrawerOpen={isCartDrawerOpen}
          handleClose={() => handleClose(false)}
        />
      )}
      {!isCartLoading && !cart?.cartItems?.length && (
        <EmptyCartDrawer
          onClick={() => handleClose(false)}
          isDrawerOpen={isCartDrawerOpen}
          handleClose={handleClose}
        />
      )}
      {!!cart?.cartItems?.length && !currentSignupStep && (
        <CartDrawer
          cart={cart}
          cartStatus={cartStatus}
          setCartStatus={setCartStatus}
          subscriptionStatus={user?.status}
          isActive={isActive}
          isDrawerOpen={isCartDrawerOpen}
          handleClose={handleClose}
          handleBeginSignup={() => {
            if (cart) {
              gtmTrackBeginCheckout(cart);
            }
            setCurrentSignupStep(SignupStep.CREATE_ACCOUNT);
          }}
        />
      )}
      {!!cart?.cartItems?.length && !!currentSignupStep && (
        <SignupDrawerContainer
          setSignInModalVisible={setSignInModalVisible}
          userAppliedCouponCode={cart.priceSummary.userAppliedCoupon}
        />
      )}
    </>
  );
}

export default CartDrawerContainer;
